import React from 'react';
import { Alert, Button, Input } from 'antd';
import { SynonymRule } from '../../../lib/api/searchDictionary';
import { SettingsModal } from '../../pgProviderRoute/common/SettingsModal';
import { useSynonymEditor } from '../hooks/useSynonymEditor';
import { RuleFeedback } from './RuleFeedback';
import { SaveResultBox } from './SaveResultBox';
import { SearchDictionaryStyle } from './styles';
import { SynonymPreviewBox } from './SynonymPreviewBox';
import { TermTags } from './TermTags';

type SynonymQuickEditModalProps = {
  rule: SynonymRule;
  canUpdate: boolean;
  onClose: () => void;
  onOpenDetail: (ruleId: string) => void;
};

/**
 * 빠른 편집 (S3-D) — 토큰 한두 개 고치는 잦은 일을 목록을 떠나지 않고 끝낸다.
 * 경고는 요약만, 미리보기는 대표어 하나만 보여 준다. 자세한 확인·이력·삭제는 상세 페이지에서.
 */
export const SynonymQuickEditModal = (props: SynonymQuickEditModalProps): React.JSX.Element => {
  const { rule, canUpdate, onClose, onOpenDetail } = props;
  const editor = useSynonymEditor({ rule });
  const representative = editor.terms[0] ?? rule.terms[0];

  return (
    <SettingsModal
      open
      width={720}
      title={
        <span>
          빠른 편집{' '}
          <span style={{ marginLeft: 4, fontSize: 14, fontWeight: 400, color: '#626e7f' }}>· {rule.no}번 줄</span>
        </span>
      }
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="link" onClick={() => onOpenDetail(rule.id)} style={{ paddingLeft: 0 }}>
            상세에서 보기 ›
          </Button>
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>닫기</Button>
          {canUpdate && editor.isEditable && (
            <Button
              type="primary"
              disabled={!editor.canSave}
              loading={editor.isSaving}
              onClick={() => void editor.save()}
            >
              {editor.saveLabel}
            </Button>
          )}
        </div>
      }
    >
      <SearchDictionaryStyle className="quick-edit-body">
        {!editor.isEditable && (
          <Alert
            type="info"
            showIcon
            message="명시적 매핑(=>) 규칙은 화면에서 고칠 수 없습니다. 상세에서 삭제 후 새로 등록해 주세요."
          />
        )}
        <div className="field-stack">
          <label className="settings-label" htmlFor="quick-edit-terms">
            토큰 <span className="settings-muted">· 쉼표로 구분, 같은 뜻끼리</span>
          </label>
          <Input.TextArea
            id="quick-edit-terms"
            autoSize={{ minRows: 2, maxRows: 5 }}
            value={editor.text}
            // 저장 중엔 잠근다 — 보낸 뒤에 고친 내용은 성공 응답에 덮인다
            disabled={!canUpdate || !editor.isEditable || editor.isSaving}
            onChange={(event) => editor.setText(event.target.value)}
          />
          <TermTags terms={editor.terms} warnings={editor.warnings} />
          <RuleFeedback
            compact
            errors={editor.errors}
            warnings={editor.warnings}
            conflict={editor.conflict}
            message={editor.message}
            onReload={editor.reload}
          />
        </div>
        {representative && (
          <div>
            <div className="block-title">
              <h3>미리보기</h3>
              <span className="settings-muted">대표어(첫 토큰)로 검색하면</span>
            </div>
            <SynonymPreviewBox
              fixedText={representative}
              // 바꿨을 때만 초안으로 분석한다 — 안 바꿨으면 저장된 그대로(매핑 규칙의 방향도 그대로) 본다
              draft={editor.isDirty ? { terms: editor.terms, ruleId: rule.id } : undefined}
            />
          </div>
        )}
        {editor.lastSave && (
          <SaveResultBox result={editor.lastSave} onUndo={() => void editor.undo()} isUndoing={editor.isSaving} />
        )}
      </SearchDictionaryStyle>
    </SettingsModal>
  );
};
